"use client";

import { useLayout, useNotification } from "@/providers";
import { useEffect, useRef, useState } from "react";

import {
  Button,
  ButtonIcon,
  Icon,
  Image,
  Input,
  Modal,
  Text,
  View
} from "@/components";

import type { JSX } from "react";

const formatFileSize = (size: number) => {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  if (size === 0) return "n/a";
  const i = parseInt(String(Math.floor(Math.log(size) / Math.log(1024))), 10);
  return `${Math.round(size / 1024 ** i)} ${sizes[i]}`;
};

const isValidFile = (file: File, accept: string) => {
  if (!accept) return true;
  const acceptedTypes = accept.split(",").map((t) => t.trim().toLowerCase());
  if (acceptedTypes.length === 0 || acceptedTypes.includes("*")) return true;

  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();

  return acceptedTypes.some((type) => {
    if (type.startsWith(".")) {
      return fileName.endsWith(type);
    }
    if (type.endsWith("/*")) {
      const baseType = type.split("/")[0];
      return fileType.startsWith(`${baseType}/`);
    }
    return fileType === type;
  });
};

export interface InputFileUploadProps {
  id: string;
  name: string;
  label?: string;
  className?: string;
  accept?: string;
  onUpload?: (file: File | null) => void;
  resetAfterUpload?: boolean;
}

/**
 * @name InputFileUpload
 * @description Flexible file upload component that handles both images (with preview) and general files.
 * Defaults to image-only mode for avatar use cases.
 *
 * @param {InputFileUploadProps} props - Component props.
 * @returns {JSX.Element} The rendered component.
 */
const InputFileUpload = ({
  id,
  name,
  label,
  className = "",
  accept = "image/*",
  onUpload,
  resetAfterUpload = false
}: InputFileUploadProps): JSX.Element => {
  const { t } = useLayout();
  const { notify } = useNotification();
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const url = preview;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [preview]);

  const clearInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFile = (file: File) => {
    if (accept && !isValidFile(file, accept)) {
      notify(
        "warning",
        `Tipo de archivo no permitido. Tipos aceptados: ${accept}`
      );
      clearInput();
      return;
    }

    onUpload?.(file);

    if (resetAfterUpload) {
      clearInput();
      return;
    }

    setSelectedFile(file);

    if (file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      setPreview(url);
    } else {
      setPreview(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const removeFile = () => {
    setSelectedFile(null);
    setPreview(null);
    clearInput();
    onUpload?.(null);
  };

  const isImage = selectedFile?.type.startsWith("image/");
  const isOnlyImages = accept
    .split(",")
    .every((a) => a.trim().startsWith("image/"));

  return (
    <View className={className}>
      {label && (
        <Text
          type="label"
          htmlFor={id}
          className="md:text-lg font-medium block mb-4"
        >
          {label}
        </Text>
      )}

      <View
        className={`
          relative w-full border-2 border-dashed rounded-lg transition-colors duration-300 flex flex-col items-center justify-center p-4 cursor-pointer
          ${selectedFile ? "h-auto border-solid" : "h-32"}
          ${
            isDragging
              ? "border-primary bg-primary/10"
              : "border-border bg-transparent hover:bg-muted/50"
          }
        `}
      >
        <Input
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          ref={fileInputRef}
          id={id}
          name={name}
          type="file"
          accept={accept}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          onChange={handleFileChange}
        />

        {selectedFile ? (
          <View className="w-full">
            {isImage && preview ? (
              <View className="flex flex-col items-center gap-4 w-full">
                <View className="relative group">
                  <View className="relative shadow-lg rounded-lg overflow-hidden bg-background ring-1 ring-border/50 max-w-[200px] z-20">
                    <Image
                      src={preview}
                      alt="Preview"
                      className="w-full h-auto object-contain cursor-pointer"
                      onClick={() => setIsModalOpen(true)}
                    />
                  </View>
                  <ButtonIcon
                    iconLeft={{ name: "x", action: removeFile }}
                    className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-full p-1.5 shadow-sm z-30"
                    label={t("delete")}
                  />
                </View>
                <Text type="p" className="text-sm font-medium">
                  {selectedFile.name}
                </Text>
              </View>
            ) : (
              <View className="flex items-center gap-4 border border-border rounded-lg p-4 hover:border-primary transition-colors w-full relative z-20">
                <Icon name="fa-solid fa-file" className="text-primary" />
                <View className="flex-1">
                  <Text type="p" className="font-bold">
                    {selectedFile.name}
                  </Text>
                  <Text type="p" className="text-sm text-muted-foreground">
                    {formatFileSize(selectedFile.size)}
                  </Text>
                </View>
                <Button
                  action={removeFile}
                  className="text-primary text-sm font-medium hover:underline"
                  label={t("delete")}
                />
              </View>
            )}
          </View>
        ) : (
          <View className="flex flex-col items-center text-center space-y-2 pointer-events-none">
            <View className="p-2 bg-muted rounded-full">
              <Icon
                name={
                  isOnlyImages
                    ? "image:fa:regular"
                    : "fa-solid fa-cloud-arrow-up"
                }
                size="2x"
                className="text-muted-foreground"
              />
            </View>
            <View>
              <Text type="p" className="text-sm font-medium">
                {isOnlyImages ? t("dropImage") : t("dropFile")}
              </Text>
            </View>
          </View>
        )}
      </View>

      <Modal
        open={isModalOpen}
        toggleOpen={setIsModalOpen}
        width="!fixed !top-1/2 !left-1/2 !-translate-x-1/2 !-translate-y-1/2 !pt-12 bg-card w-full max-w-2xl max-h-[90vh] border border-border/50 shadow-2xl rounded-xl overflow-y-auto"
      >
        {preview && (
          <View className="flex flex-col h-full">
            <View className="flex items-center justify-between px-6 py-4 border-b border-border/40 bg-muted/30">
              <Text
                type="h3"
                className="text-lg font-semibold flex items-center gap-2"
              >
                <Icon name="fa-solid fa-image" className="text-primary" />
                {t("preview")}
              </Text>
            </View>

            <View className="p-8 flex flex-col items-center justify-center bg-zinc-950/5 dark:bg-black/20">
              <View className="relative shadow-lg rounded-lg overflow-hidden bg-background ring-1 ring-border/50">
                <Image
                  src={preview}
                  alt="Preview"
                  className="max-w-full max-h-[60vh] object-contain"
                />
              </View>

              <View className="mt-6 flex flex-col items-center gap-1">
                <Text
                  type="h2"
                  className="text-base font-medium text-center px-4"
                >
                  {selectedFile?.name}
                </Text>
                {selectedFile && (
                  <Text
                    type="h2"
                    className="text-xs text-muted-foreground uppercase tracking-wider font-semibold"
                  >
                    {formatFileSize(selectedFile.size)}
                  </Text>
                )}
              </View>
            </View>
          </View>
        )}
      </Modal>
    </View>
  );
};

export default InputFileUpload;
